import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi, euro, errMsg, statusLabel, isPickup } from "@/lib/api";
import { StatusBadge } from "@/pages/Account";
import { ShippingBox, PickupBox, ReturnBox, RETURN_STATUS } from "@/components/admin/OrderExtras";
import { BankSettings, PendingSummary, hoursLeft } from "@/components/admin/PendingPayments";

const STATUSES = ["pagado", "enviado", "entregado", "cancelado"];

export const OrdersTab = () => {
  const call = useApi();
  const [orders, setOrders] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => call("get", "/admin/orders").then((r) => setOrders(r.data)).catch((e) => { toast.error(errMsg(e, "No se pudieron cargar los pedidos")); setOrders((o) => o || []); }), [call]);
  useEffect(() => { load(); }, [load]);

  const change = async (id, status) => {
    try { await call("patch", `/admin/orders/${id}`, { status }); toast.success("Pedido actualizado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const resend = async (id) => {
    const t = toast.loading("Enviando email…");
    try { await call("post", `/admin/orders/${id}/resend-confirmation`); toast.success("Email de confirmación enviado", { id: t }); load(); }
    catch (e) { toast.error(errMsg(e), { id: t }); }
  };

  const confirmPay = async (o) => {
    if (!window.confirm(`¿Has recibido ${euro(o.total)} por ${o.payment_method === "bizum" ? "Bizum" : "transferencia"} del pedido ${o.number}?`)) return;
    try { await call("post", `/admin/orders/${o.id}/confirm-payment`); toast.success("Pago confirmado. Enviando email al cliente…"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (orders === null) return <p className="py-16 text-center text-slate-500" data-testid="admin-orders-loading">Cargando pedidos…</p>;
  if (!orders.length) return <p className="py-16 text-center text-slate-500" data-testid="admin-orders-empty">Todavía no hay pedidos.</p>;

  return (
    <div className="space-y-3" data-testid="admin-orders-tab">
      <BankSettings />
      <PendingSummary orders={orders} />
      {orders.map((o) => (
        <div key={o.id} className="bg-white border" data-testid={`admin-order-${o.number}`}>
          <div className="p-4 flex flex-wrap items-center gap-4">
            <button className="font-mono font-bold text-sm hover:text-brand" onClick={() => setOpenId(openId === o.id ? null : o.id)} data-testid={`admin-order-toggle-${o.number}`}>{o.number}</button>
            <span className="text-sm">{o.customer.name}</span>
            <span className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("es-ES")}</span>
            <StatusBadge status={o.status} order={o} />
            {o.return_request && <span className={`tag ${RETURN_STATUS[o.return_request.status]?.cls}`} data-testid={`admin-order-return-badge-${o.number}`}>{RETURN_STATUS[o.return_request.status]?.label}</span>}
            <span className="ml-auto font-bold">{euro(o.total)}</span>
            {o.payment_method && <span className="tag bg-slate-100 text-slate-600" data-testid={`admin-order-method-${o.number}`}>{o.payment_method === "bizum" ? "Bizum" : "Transferencia"}</span>}
            {isPickup(o) && <span className="tag bg-emerald-50 text-verde" data-testid={`admin-order-pickup-${o.number}`}>Recogida en tienda</span>}
            {o.status === "pendiente_pago" && o.expires_at && <span className="text-xs font-semibold text-amber-700" data-testid={`admin-order-expires-${o.number}`}>caduca en {hoursLeft(o.expires_at)} h</span>}
            {o.status === "pendiente_pago" ? (
              <>
                <button onClick={() => confirmPay(o)} className="btn-primary !h-9 !px-4 text-sm" data-testid={`admin-order-confirm-pay-${o.number}`}>Confirmar pago</button>
                <button onClick={() => window.confirm(`¿Cancelar el pedido ${o.number}? El stock vuelve a la venta.`) && change(o.id, "cancelado")} className="text-xs font-semibold underline hover:text-rose-600" data-testid={`admin-order-cancel-${o.number}`}>Cancelar</button>
              </>
            ) : (
            <select value={o.status} onChange={(e) => change(o.id, e.target.value)} className="h-9 border rounded-full px-3 text-sm" data-testid={`admin-order-status-${o.number}`}>
              {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s, o)}</option>)}
            </select>
            )}
          </div>
          {openId === o.id && (
            <div className="border-t p-4 grid sm:grid-cols-2 gap-6 text-sm bg-slate-50">
              <div>
                <p className="filter-title">{isPickup(o) ? "Recogida en tienda" : "Envío"}</p>
                {isPickup(o)
                  ? <p data-testid={`admin-order-contact-${o.number}`}>{o.customer.name}<br />{o.customer.phone}{o.customer.email && <><br />{o.customer.email}</>}</p>
                  : <p>{o.customer.name}<br />{o.customer.address}<br />{o.customer.postal_code} {o.customer.city}<br />{o.customer.email} · {o.customer.phone}</p>}
                {o.customer.invoice && <p className="mt-2 font-semibold text-brand" data-testid={`order-invoice-${o.number}`}>Quiere factura: {o.customer.company || o.customer.name} · NIF {o.customer.tax_id}</p>}
                {o.customer.notes && <p className="mt-2 italic text-slate-500">"{o.customer.notes}"</p>}
                {o.customer.email ? (
                <div className="mt-4 flex items-center gap-3">
                  <span className={`tag ${o.confirmation_email === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`admin-order-email-status-${o.number}`}>
                    {o.confirmation_email === "sent" ? "Email de confirmación enviado" : "Email no enviado"}
                  </span>
                  <button onClick={() => resend(o.id)} className="text-xs font-semibold underline hover:text-brand" data-testid={`admin-order-resend-${o.number}`}>Reenviar</button>
                </div>
                ) : <p className="mt-4 text-xs text-slate-500" data-testid={`admin-order-noemail-${o.number}`}>Cliente sin email: avisar por teléfono.</p>}
                {isPickup(o) ? <PickupBox order={o} onDone={load} /> : <ShippingBox order={o} onDone={load} />}
              </div>
              <div>
                <p className="filter-title">Artículos</p>
                {o.items.map((i, k) => <p key={k}>{i.quantity}× {i.name}{i.reference && ` [${i.reference}]`} (T. {i.size}{i.color && `, ${i.color}`}) — {euro(i.price * i.quantity)}</p>)}
                <p className="mt-2 text-slate-500">{isPickup(o) ? "Sin portes (recogida en tienda)" : `Envío: ${o.shipping ? euro(o.shipping) : "Gratis"}`}</p>
              </div>
              <ReturnBox order={o} onDone={load} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
